import { Card, Hero, Section } from '../components/ui';

const termsToc = [
  { id: 'acceptance-of-terms', number: '1', title: 'Acceptance of Terms' },
  { id: 'description-of-services', number: '2', title: 'Description of Services' },
  { id: 'account-registration', number: '3', title: 'Account Registration' },
  { id: 'acceptable-use', number: '4', title: 'Acceptable Use' },
  { id: 'fees-and-payment', number: '5', title: 'Fees and Payment' },
  { id: 'data-ownership', number: '6', title: 'Data Ownership and Usage' },
  { id: 'service-level-agreement', number: '7', title: 'Service Level Agreement (SLA)' },
  { id: 'warranties', number: '8', title: 'Warranties and Disclaimers' },
  { id: 'limitation-of-liability', number: '9', title: 'Limitation of Liability' },
  { id: 'indemnification', number: '10', title: 'Indemnification' },
  { id: 'termination', number: '11', title: 'Termination' },
  { id: 'governing-law', number: '12', title: 'Governing Law' },
  { id: 'changes-to-terms', number: '13', title: 'Changes to Terms' },
  { id: 'contact-information', number: '14', title: 'Contact Information' }
];

export const Terms = () => {
  return (
    <>
      <Hero title="Terms of Service" description="Last updated: January 2025" size="sm" />

      <Section>
        <div className="legal-layout">
          <aside className="legal-sidebar">
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">At a glance</h3>
              <div className="legal-sidebar__meta">
                <span>Last updated: January 2025</span>
                <span>Applies to all textadapt customers</span>
              </div>
            </Card>
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">Quick navigation</h3>
              <ul className="legal-sidebar__links">
                {termsToc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span>{item.number}</span>
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">Questions?</h3>
              <div className="legal-sidebar__meta">
                <span>Email legal@textadapt.com for contract questions</span>
                <span>Enterprise terms available upon request</span>
              </div>
            </Card>
          </aside>
          <article className="legal-content">
            <h2 id="acceptance-of-terms">1. Acceptance of Terms</h2>
            <p>
              By accessing or using textadapt's document processing platform and services (the "Services"), you agree to
              be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Services.
            </p>

            <h2 id="description-of-services">2. Description of Services</h2>
            <p>
              textadapt provides cloud-based document processing services, including OCR, data extraction, validation,
              and ERP integration capabilities. We reserve the right to modify, suspend, or discontinue any feature at any
              time with reasonable notice.
            </p>

            <h2 id="account-registration">3. Account Registration</h2>
            <h3 id="eligibility">3.1 Eligibility</h3>
            <p>
              You must be at least 18 years old and authorized to bind your organization to these Terms. By registering,
              you represent that all information provided is accurate and complete.
            </p>

            <h3 id="account-security">3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities
              under your account. Notify us immediately of any unauthorized access.
            </p>

            <h2 id="acceptable-use">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Violate any laws or regulations</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Reverse engineer, decompile, or disassemble the Services</li>
              <li>Use the Services to process documents you do not have rights to</li>
              <li>Resell or redistribute the Services without written permission</li>
              <li>Exceed rate limits or abuse API endpoints</li>
            </ul>

            <h2 id="fees-and-payment">5. Fees and Payment</h2>
            <h3 id="pricing">5.1 Pricing</h3>
            <p>
              Fees are based on your selected plan and document volume. Pricing is available at textadapt.com/pricing and
              may change with 30 days' notice for existing customers.
            </p>

            <h3 id="billing">5.2 Billing</h3>
            <p>
              Subscription fees are billed monthly or annually in advance. Overage charges for documents exceeding plan
              limits are billed in arrears. All fees are non-refundable except as required by law.
            </p>

            <h3 id="late-payment">5.3 Late Payment</h3>
            <p>
              Accounts with overdue invoices may be suspended after 15 days. We may charge interest on late payments at
              the lesser of 1.5% per month or the maximum rate permitted by law.
            </p>

            <h2 id="data-ownership">6. Data Ownership and Usage</h2>
            <h3 id="your-data">6.1 Your Data</h3>
            <p>
              You retain all ownership rights to documents and data you upload ("Customer Data"). You grant us a license
              to process Customer Data solely to provide the Services.
            </p>

            <h3 id="model-training">6.2 Model Training</h3>
            <p>
              We may use anonymized, aggregated data to improve our machine learning models. Customer Data is never
              shared with other customers or used for training without explicit consent.
            </p>

            <h2 id="service-level-agreement">7. Service Level Agreement (SLA)</h2>
            <p>
              We target 99.9% uptime for Professional and Enterprise plans (excluding scheduled maintenance). SLA credits
              are available for eligible downtime. Full SLA terms are available in your plan documentation.
            </p>

            <h2 id="warranties">8. Warranties and Disclaimers</h2>
            <p>
              THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR
              IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT
              GUARANTEE 100% EXTRACTION ACCURACY AND RECOMMEND HUMAN REVIEW FOR CRITICAL WORKFLOWS.
            </p>

            <h2 id="limitation-of-liability">9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, textadapt'S TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU IN
              THE 12 MONTHS PRECEDING THE CLAIM. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES.
            </p>

            <h2 id="indemnification">10. Indemnification</h2>
            <p>
              You agree to indemnify and hold textadapt harmless from claims arising from your use of the Services, your
              Customer Data, or your violation of these Terms.
            </p>

            <h2 id="termination">11. Termination</h2>
            <p>
              Either party may terminate with 30 days' notice. We may suspend or terminate your account immediately for
              violations of these Terms. Upon termination, you have 30 days to export your data before deletion.
            </p>

            <h2 id="governing-law">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to conflict of law principles.
              Disputes shall be resolved in the state or federal courts located in San Francisco County, California.
            </p>

            <h2 id="changes-to-terms">13. Changes to Terms</h2>
            <p>
              We may update these Terms at any time. Material changes will be communicated via email 30 days before taking
              effect. Continued use after changes constitutes acceptance.
            </p>

            <h2 id="contact-information">14. Contact Information</h2>
            <p>For questions about these Terms, contact us at:</p>
            <ul className="legal-list-plain">
              <li>
                <strong>Email:</strong> <a href="mailto:legal@textadapt.com">legal@textadapt.com</a>
              </li>
              <li>
                <strong>Address:</strong> textadapt Inc., 123 Enterprise Ave, San Francisco, CA 94105
              </li>
            </ul>
          </article>
        </div>
      </Section>
    </>
  );
};
